import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
  CourierAgreement,
  CourierProfile,
  CourierTerms,
  CourierZone,
  FEE_LINE_DISTANCE_UNKNOWN,
  FeeResult,
  courierModuleService,
  emptyTerms,
  formatMoney,
} from './courierModuleService';

interface Props {
  open: boolean;
  courier: CourierProfile | null;
  onClose: () => void;
  onSaved: () => void;
  /** Bölge yoksa kullanıcıyı bölgeler ekranına yönlendirmek için. */
  onOpenZones: () => void;
}

const toNumber = (v: string): number | null => {
  if (v.trim() === '') return null;
  const n = Number(v.replace(',', '.'));
  return Number.isFinite(n) ? n : null;
};

const MoneyField: React.FC<{
  label: string;
  value: number | null | undefined;
  onChange: (v: number | null) => void;
  suffix?: string;
  width?: number;
}> = ({ label, value, onChange, suffix = '₺', width = 150 }) => (
  <TextField
    size="small"
    label={label}
    value={value ?? ''}
    onChange={(e) => onChange(toNumber(e.target.value))}
    inputProps={{ inputMode: 'decimal' }}
    InputProps={{ endAdornment: <InputAdornment position="end">{suffix}</InputAdornment> }}
    sx={{ width }}
  />
);

const formatDecimal = (v: number): string => String(v).replace('.', ',');

/** Ondalık alan: virgülle yazılabilir ("4,1"); yazarken metin korunur, dışarıya sayı verilir. */
const DecimalField: React.FC<{
  label?: string;
  value: number | null | undefined;
  onChange: (v: number | null) => void;
  suffix?: string;
  width?: number;
  disabled?: boolean;
}> = ({ label, value, onChange, suffix, width = 120, disabled }) => {
  const [text, setText] = useState(value == null ? '' : formatDecimal(value));
  useEffect(() => {
    // Dışarıdan değişti (ör. sürüm şablonu): yazılan metin aynı sayıyı tutuyorsa (ör. "4,") dokunulmaz
    setText((prev) => (toNumber(prev) === (value ?? null) ? prev : value == null ? '' : formatDecimal(value)));
  }, [value]);
  return (
    <TextField
      size="small"
      label={label}
      value={text}
      disabled={disabled}
      onChange={(e) => {
        const v = e.target.value.replace(/[^0-9.,]/g, '');
        setText(v);
        onChange(toNumber(v));
      }}
      inputProps={{ inputMode: 'decimal' }}
      InputProps={suffix ? { endAdornment: <InputAdornment position="end">{suffix}</InputAdornment> } : undefined}
      sx={{ width }}
    />
  );
};

type DistanceMode = 'percent' | 'amount';

const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const nowLocalIso = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
};

const AgreementDialog: React.FC<Props> = ({ open, courier, onClose, onSaved, onOpenZones }) => {
  const { t } = useTranslation('salesSystem');
  const [terms, setTerms] = useState<CourierTerms>(emptyTerms());
  const [validFrom, setValidFrom] = useState(todayIso());
  const [versions, setVersions] = useState<CourierAgreement[]>([]);
  const [zones, setZones] = useState<CourierZone[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Örnek hesap
  const [sampleZone, setSampleZone] = useState<number | ''>('');
  const [sampleTime, setSampleTime] = useState(nowLocalIso());
  const [sampleWeather, setSampleWeather] = useState(false);
  const [sampleDistance, setSampleDistance] = useState<number | null>(4.35);
  const [preview, setPreview] = useState<FeeResult | null>(null);
  // Mesafe ek ücreti: yüzde mi tutar mı (ikisi birden girilmez)
  const [distanceMode, setDistanceMode] = useState<DistanceMode>('percent');
  const previewTimer = useRef<number | undefined>(undefined);

  const isOwn = courier?.kind === 'OWN';

  useEffect(() => {
    if (!open || !courier) return;
    setError(null);
    setLoading(true);
    setValidFrom(todayIso());
    Promise.all([courierModuleService.listAgreements(courier.id), courierModuleService.listZones()])
      .then(([list, allZones]) => {
        setVersions(list);
        const mine = allZones.filter((z) => courier.branchIds.length === 0 || courier.branchIds.includes(z.branchId));
        setZones(mine);
        const current = list.find((a) => a.isCurrent) || list[0];
        setTerms(current ? { ...emptyTerms(), ...current.terms } : emptyTerms());
        setSampleZone(mine[0]?.id ?? '');
      })
      .catch((e) => setError(e?.message))
      .finally(() => setLoading(false));
  }, [open, courier]);

  // Örnek hesap: koşullar değişince sunucudaki aynı hesap motoruyla hesaplanır
  useEffect(() => {
    if (!open) return;
    window.clearTimeout(previewTimer.current);
    previewTimer.current = window.setTimeout(() => {
      courierModuleService
        .feePreview({
          terms,
          zoneId: sampleZone === '' ? null : sampleZone,
          localTime: sampleTime,
          badWeather: sampleWeather,
          distanceKm: terms.distance ? sampleDistance : null,
        })
        .then(setPreview)
        .catch(() => setPreview(null));
    }, 350);
    return () => window.clearTimeout(previewTimer.current);
  }, [open, terms, sampleZone, sampleTime, sampleWeather, sampleDistance]);

  // Kayıtlı anlaşma tutarla geldiyse "TL" seçili açılır
  useEffect(() => {
    if (terms.distance?.amount != null) setDistanceMode('amount');
    else if (terms.distance?.percent != null) setDistanceMode('percent');
  }, [terms.distance?.amount, terms.distance?.percent]);

  const set = (patch: Partial<CourierTerms>) => setTerms((prev) => ({ ...prev, ...patch }));

  const setDistanceKind = (mode: DistanceMode) => {
    setDistanceMode(mode);
    const d = terms.distance;
    if (!d) return;
    set({
      distance: {
        ...d,
        percent: mode === 'percent' ? d.percent ?? 100 : null,
        amount: mode === 'amount' ? d.amount ?? null : null,
      },
    });
  };

  const zonePrice = (zoneId: number) => terms.zonePrices.find((z) => z.zoneId === zoneId)?.price ?? null;
  const setZonePrice = (zoneId: number, price: number | null) =>
    set({
      zonePrices:
        price == null
          ? terms.zonePrices.filter((z) => z.zoneId !== zoneId)
          : [...terms.zonePrices.filter((z) => z.zoneId !== zoneId), { zoneId, price }],
    });

  const surchargeRow = (
    key: 'weekend' | 'badWeather',
    label: string
  ) => {
    const value = terms[key];
    return (
      <Row label={label} on={!!value} onToggle={(on) => set({ [key]: on ? { percent: null, amount: null } : null } as any)}>
        {value && (
          <>
            <MoneyField label="%" suffix="%" width={110} value={value.percent} onChange={(v) => set({ [key]: { ...value, percent: v } } as any)} />
            <MoneyField label={t('courierModule.agreement.amount')} width={130} value={value.amount} onChange={(v) => set({ [key]: { ...value, amount: v } } as any)} />
          </>
        )}
      </Row>
    );
  };

  const save = async () => {
    if (!courier) return;
    setSaving(true);
    setError(null);
    try {
      await courierModuleService.addAgreement(courier.id, terms, validFrom);
      onSaved();
      onClose();
    } catch (e: any) {
      setError(e?.message || t('courierModule.errors.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const current = versions.find((v) => v.isCurrent);

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        {t('courierModule.agreement.title', { name: courier?.name || '' })}
        {courier && <Chip size="small" label={t(`courierModule.kinds.${courier.kind}`)} />}
        {current && (
          <Chip
            size="small"
            variant="outlined"
            label={t('courierModule.agreement.currentVersion', {
              version: current.version,
              date: new Date(current.validFrom).toLocaleDateString('tr-TR'),
            })}
          />
        )}
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'grid', placeItems: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) 280px' }, gap: 2 }}>
            <Stack spacing={1.25}>
              {error && <Alert severity="error">{error}</Alert>}
              {isOwn && <Alert severity="info">{t('courierModule.agreement.ownNote')}</Alert>}

              <Row
                label={isOwn ? t('courierModule.agreement.ownBonus') : t('courierModule.agreement.perPackage')}
                on={terms.perPackageFee != null}
                onToggle={(on) => set({ perPackageFee: on ? 0 : null })}
              >
                {terms.perPackageFee != null && (
                  <MoneyField label={t('courierModule.agreement.perPackageShort')} value={terms.perPackageFee} onChange={(v) => set({ perPackageFee: v ?? 0 })} />
                )}
              </Row>

              {!isOwn && (
                <Row label={t('courierModule.agreement.zonePricing')} on={terms.zonePricing} onToggle={(on) => set({ zonePricing: on })}>
                  {terms.zonePricing && (
                    <Box sx={{ width: '100%' }}>
                      {zones.length === 0 ? (
                        <Alert
                          severity="warning"
                          action={
                            <Button size="small" onClick={onOpenZones}>
                              {t('courierModule.zones.open')}
                            </Button>
                          }
                        >
                          {t('courierModule.agreement.noZones')}
                        </Alert>
                      ) : (
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 1 }}>
                          {zones.map((z) => (
                            <MoneyField key={z.id} label={z.name} width={170} value={zonePrice(z.id)} onChange={(v) => setZonePrice(z.id, v)} />
                          ))}
                          <MoneyField
                            label={t('courierModule.agreement.unmatchedZone')}
                            width={170}
                            value={terms.unmatchedZoneFee}
                            onChange={(v) => set({ unmatchedZoneFee: v })}
                          />
                        </Box>
                      )}
                    </Box>
                  )}
                </Row>
              )}

              {!isOwn && (
                <>
                  <Row label={t('courierModule.agreement.hourly')} on={terms.hourlyRate != null} onToggle={(on) => set({ hourlyRate: on ? 0 : null })}>
                    {terms.hourlyRate != null && (
                      <MoneyField label={t('courierModule.agreement.perHour')} value={terms.hourlyRate} onChange={(v) => set({ hourlyRate: v ?? 0 })} />
                    )}
                  </Row>
                  <Row
                    label={t('courierModule.agreement.fixed')}
                    on={terms.dailyFixed != null || terms.monthlyFixed != null}
                    onToggle={(on) => set(on ? { dailyFixed: 0 } : { dailyFixed: null, monthlyFixed: null })}
                  >
                    {(terms.dailyFixed != null || terms.monthlyFixed != null) && (
                      <>
                        <MoneyField label={t('courierModule.agreement.daily')} value={terms.dailyFixed} onChange={(v) => set({ dailyFixed: v })} />
                        <MoneyField label={t('courierModule.agreement.monthly')} value={terms.monthlyFixed} onChange={(v) => set({ monthlyFixed: v })} />
                      </>
                    )}
                  </Row>
                  <Row label={t('courierModule.agreement.guarantee')} on={terms.dailyGuarantee != null} onToggle={(on) => set({ dailyGuarantee: on ? 0 : null })}>
                    {terms.dailyGuarantee != null && (
                      <MoneyField label={t('courierModule.agreement.perDay')} value={terms.dailyGuarantee} onChange={(v) => set({ dailyGuarantee: v ?? 0 })} />
                    )}
                  </Row>
                </>
              )}

              <Divider textAlign="left">
                <Typography variant="caption" color="text.secondary">
                  {t('courierModule.agreement.extras')}
                </Typography>
              </Divider>

              <Row
                label={t('courierModule.agreement.night')}
                on={!!terms.night}
                onToggle={(on) => set({ night: on ? { start: '23:00', end: '06:00', percent: 20, amount: null } : null })}
              >
                {terms.night && (
                  <>
                    <TextField size="small" type="time" label={t('courierModule.agreement.start')} value={terms.night.start}
                      onChange={(e) => set({ night: { ...terms.night!, start: e.target.value } })} sx={{ width: 120 }} InputLabelProps={{ shrink: true }} />
                    <TextField size="small" type="time" label={t('courierModule.agreement.end')} value={terms.night.end}
                      onChange={(e) => set({ night: { ...terms.night!, end: e.target.value } })} sx={{ width: 120 }} InputLabelProps={{ shrink: true }} />
                    <MoneyField label="%" suffix="%" width={100} value={terms.night.percent} onChange={(v) => set({ night: { ...terms.night!, percent: v } })} />
                    <MoneyField label={t('courierModule.agreement.amount')} width={120} value={terms.night.amount} onChange={(v) => set({ night: { ...terms.night!, amount: v } })} />
                  </>
                )}
              </Row>
              {surchargeRow('weekend', t('courierModule.agreement.weekend'))}
              {surchargeRow('badWeather', t('courierModule.agreement.badWeather'))}

              <Row
                label={t('courierModule.agreement.zoneExtra')}
                on={terms.zoneSurcharges.length > 0}
                onToggle={(on) =>
                  set({ zoneSurcharges: on && zones[0] ? [{ zoneId: zones[0].id, amount: 20, percent: null }] : [] })
                }
                disabled={zones.length === 0}
              >
                {terms.zoneSurcharges.length > 0 && (
                  <Stack spacing={1} sx={{ width: '100%' }}>
                    {terms.zoneSurcharges.map((zs, i) => (
                      <Stack key={i} direction="row" spacing={1} alignItems="center">
                        <TextField select size="small" label={t('courierModule.agreement.zone')} value={zs.zoneId} sx={{ width: 180 }}
                          onChange={(e) => {
                            const list = [...terms.zoneSurcharges];
                            list[i] = { ...zs, zoneId: Number(e.target.value) };
                            set({ zoneSurcharges: list });
                          }}>
                          {zones.map((z) => (
                            <MenuItem key={z.id} value={z.id}>{z.name}</MenuItem>
                          ))}
                        </TextField>
                        <MoneyField label={t('courierModule.agreement.amount')} width={120} value={zs.amount}
                          onChange={(v) => {
                            const list = [...terms.zoneSurcharges];
                            list[i] = { ...zs, amount: v };
                            set({ zoneSurcharges: list });
                          }} />
                        <MoneyField label="%" suffix="%" width={100} value={zs.percent}
                          onChange={(v) => {
                            const list = [...terms.zoneSurcharges];
                            list[i] = { ...zs, percent: v };
                            set({ zoneSurcharges: list });
                          }} />
                        <IconButton size="small" onClick={() => set({ zoneSurcharges: terms.zoneSurcharges.filter((_, j) => j !== i) })}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    ))}
                    <Button size="small" startIcon={<AddIcon />} sx={{ alignSelf: 'flex-start' }}
                      onClick={() => {
                        const free = zones.find((z) => !terms.zoneSurcharges.some((s) => s.zoneId === z.id));
                        if (free) set({ zoneSurcharges: [...terms.zoneSurcharges, { zoneId: free.id, amount: 20, percent: null }] });
                      }}>
                      {t('courierModule.agreement.addZoneExtra')}
                    </Button>
                  </Stack>
                )}
              </Row>

              {/* Mesafe: "[4,1] km üzeri: ( ) % [100]  ( ) TL [ ]" */}
              <Row
                label={t('courierModule.agreement.distance')}
                on={!!terms.distance}
                onToggle={(on) => {
                  setDistanceMode('percent');
                  set({ distance: on ? { aboveKm: 4.1, percent: 100, amount: null } : null });
                }}
              >
                {terms.distance && (
                  <>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <DecimalField label={t('courierModule.agreement.distanceLimit')} suffix="km" width={110} value={terms.distance.aboveKm}
                        onChange={(v) => set({ distance: { ...terms.distance!, aboveKm: v ?? 0 } })} />
                      <Typography variant="body2">{t('courierModule.agreement.distanceAbove')}</Typography>
                    </Stack>
                    <RadioGroup row value={distanceMode} onChange={(e) => setDistanceKind(e.target.value as DistanceMode)}
                      sx={{ alignItems: 'center', gap: 1 }}>
                      <FormControlLabel value="percent" control={<Radio size="small" />} label="%" sx={{ mr: 0 }} />
                      <DecimalField label="%" suffix="%" width={100} value={terms.distance.percent} disabled={distanceMode !== 'percent'}
                        onChange={(v) => set({ distance: { ...terms.distance!, percent: v, amount: null } })} />
                      <FormControlLabel value="amount" control={<Radio size="small" />} label="TL" sx={{ mr: 0, ml: 1 }} />
                      <DecimalField label={t('courierModule.agreement.amount')} suffix="₺" width={120} value={terms.distance.amount} disabled={distanceMode !== 'amount'}
                        onChange={(v) => set({ distance: { ...terms.distance!, amount: v, percent: null } })} />
                    </RadioGroup>
                    <Typography variant="caption" color="text.secondary" sx={{ width: '100%' }}>
                      {t('courierModule.agreement.distanceHint')}
                    </Typography>
                  </>
                )}
              </Row>

              <Divider />

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(4, 1fr)' }, gap: 1.5 }}>
                <TextField select size="small" label={t('courierModule.agreement.cancelRule')} value={terms.cancelRule}
                  onChange={(e) => set({ cancelRule: e.target.value as any })}>
                  {(['IF_PICKED_UP', 'ALWAYS', 'NEVER'] as const).map((r) => (
                    <MenuItem key={r} value={r}>{t(`courierModule.agreement.cancel.${r}`)}</MenuItem>
                  ))}
                </TextField>
                <TextField select size="small" label={t('courierModule.agreement.period')} value={terms.settlementPeriod}
                  onChange={(e) => set({ settlementPeriod: e.target.value as any })}>
                  {(['DAILY', 'WEEKLY', 'BIWEEKLY', 'MONTHLY'] as const).map((p) => (
                    <MenuItem key={p} value={p}>{t(`courierModule.agreement.periods.${p}`)}</MenuItem>
                  ))}
                </TextField>
                <TextField size="small" type="date" label={t('courierModule.agreement.validFrom')} value={validFrom}
                  onChange={(e) => setValidFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
                <FormControlLabel
                  control={<Switch checked={terms.vatIncluded} onChange={(e) => set({ vatIncluded: e.target.checked })} />}
                  label={terms.vatIncluded ? t('courierModule.agreement.vatIncluded') : t('courierModule.agreement.vatExcluded')}
                />
                {!terms.vatIncluded && (
                  <TextField size="small" label={t('courierModule.agreement.vatRate')} value={terms.vatRate ?? 20}
                    onChange={(e) => {
                      const v = Number(e.target.value.replace(',', '.'));
                      set({ vatRate: e.target.value === '' ? null : Number.isFinite(v) ? v : terms.vatRate ?? 20 });
                    }}
                    inputProps={{ inputMode: 'decimal' }}
                    InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }} />
                )}
              </Box>
            </Stack>

            {/* Sağ panel: örnek hesap ve sürümler */}
            <Stack spacing={2}>
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="overline" color="text.secondary">
                  {t('courierModule.agreement.sample')}
                </Typography>
                <Stack spacing={1.25} sx={{ mt: 1 }}>
                  {zones.length > 0 && (
                    <TextField select size="small" label={t('courierModule.agreement.zone')} value={sampleZone}
                      onChange={(e) => setSampleZone(e.target.value === '' ? '' : Number(e.target.value))}>
                      <MenuItem value="">{t('courierModule.agreement.noZone')}</MenuItem>
                      {zones.map((z) => (
                        <MenuItem key={z.id} value={z.id}>{z.name}</MenuItem>
                      ))}
                    </TextField>
                  )}
                  <TextField size="small" type="datetime-local" label={t('courierModule.agreement.sampleTime')} value={sampleTime}
                    onChange={(e) => setSampleTime(e.target.value)} InputLabelProps={{ shrink: true }} />
                  <FormControlLabel
                    control={<Switch size="small" checked={sampleWeather} onChange={(e) => setSampleWeather(e.target.checked)} />}
                    label={t('courierModule.agreement.badWeather')}
                  />
                  {terms.distance && (
                    <DecimalField label={t('courierModule.agreement.sampleDistance')} suffix="km" width={180} value={sampleDistance}
                      onChange={setSampleDistance} />
                  )}
                  <Divider />
                  {preview?.lines.filter((l) => l.code !== FEE_LINE_DISTANCE_UNKNOWN).map((l) => (
                    <Box key={l.code} sx={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                      <span>{t(`courierModule.feeLines.${l.code}`, { defaultValue: l.label })}</span>
                      <b>{formatMoney(l.amount)}</b>
                    </Box>
                  ))}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', borderTop: 1, borderColor: 'divider', pt: 1 }}>
                    <Typography fontWeight={700}>{t('courierModule.agreement.packageFee')}</Typography>
                    <Typography fontWeight={700}>{formatMoney(preview?.total ?? 0)}</Typography>
                  </Box>
                  {preview?.zoneMissing && <Alert severity="warning">{t('courierModule.agreement.zoneMissing')}</Alert>}
                  {preview?.distanceUnknown && <Alert severity="info">{t(`courierModule.feeLines.${FEE_LINE_DISTANCE_UNKNOWN}`)}</Alert>}
                </Stack>
              </Paper>

              {versions.length > 0 && (
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Typography variant="overline" color="text.secondary">
                    {t('courierModule.agreement.history')}
                  </Typography>
                  <Stack spacing={1} sx={{ mt: 1 }}>
                    {versions.map((v) => (
                      <Box key={v.id} sx={{ fontSize: 13 }}>
                        <b>{t('courierModule.agreement.versionLabel', { version: v.version })}</b>{' '}
                        <span>· {new Date(v.validFrom).toLocaleDateString('tr-TR')}</span>{' '}
                        {v.isCurrent && <Chip size="small" color="success" label={t('courierModule.agreement.current')} />}
                        <Typography variant="body2" color="text.secondary">
                          {v.summary}
                        </Typography>
                        <Button size="small" onClick={() => setTerms({ ...emptyTerms(), ...v.terms })}>
                          {t('courierModule.agreement.useAsTemplate')}
                        </Button>
                      </Box>
                    ))}
                  </Stack>
                </Paper>
              )}
            </Stack>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1, pl: 1 }}>
          {t('courierModule.agreement.newVersionNote')}
        </Typography>
        <Button onClick={onClose} disabled={saving}>
          {t('courierModule.actions.cancel')}
        </Button>
        <Button variant="contained" onClick={save} disabled={saving || loading}
          startIcon={saving ? <CircularProgress size={16} /> : undefined}>
          {t('courierModule.agreement.saveVersion')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/** Anlaşma ekranında açılıp kapanan bir kalem satırı. */
const Row: React.FC<{
  label: string;
  on: boolean;
  onToggle: (on: boolean) => void;
  disabled?: boolean;
  children?: React.ReactNode;
}> = ({ label, on, onToggle, disabled, children }) => (
  <Paper
    variant="outlined"
    sx={{
      p: 1,
      px: 1.5,
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      flexWrap: 'wrap',
      borderColor: on ? 'primary.main' : 'divider',
    }}
  >
    <FormControlLabel
      sx={{ minWidth: 200, mr: 0 }}
      control={<Switch checked={on} disabled={disabled} onChange={(e) => onToggle(e.target.checked)} />}
      label={<Typography fontWeight={600} fontSize={14}>{label}</Typography>}
    />
    {children}
  </Paper>
);

export default AgreementDialog;
